"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const date_fns_1 = require("date-fns");
const date_fns_tz_1 = require("date-fns-tz");
const react_1 = require("react");
const config_1 = require("config");
const merchant_management_v1_api_1 = require("merchant-management-v1-api");
const utils_1 = require("utils");
const useSalesTable = ({ locationId }) => {
    // Dates
    const todaysDate = new Date();
    const startDate = (0, date_fns_1.subDays)(todaysDate, config_1.REPORTS_START_OFFSET_DAYS);
    const endDate = new Date();
    endDate.setDate(todaysDate.getDate() + 1);
    // State
    const [displayedNextDayTransactions, setDisplayedNextDayTransactions] = (0, react_1.useState)([]);
    const [displayedPendingTransactions, setDisplayedPendingTransactions] = (0, react_1.useState)([]);
    const [postSalesReports, { data: salesReports, isError: salesReportError, isLoading: salesReportLoading, },] = (0, merchant_management_v1_api_1.usePostMerchantManagementV1ReportsSalesMutation)({
        fixedCacheKey: 'salesReports',
    });
    const shouldShowNextDayPendingAccessLoanTransaction = (transaction) => {
        return (transaction.status &&
            (0, utils_1.checkIfESTTimeisPast10Pm)(transaction.installmentServiceDate) &&
            config_1.PENDING_STATES.includes(transaction.status));
    };
    const handleFilters = (filterCriteria, data) => {
        const { filterOption, searchField } = filterCriteria;
        if (!searchField) {
            return data;
        }
        const filteredData = data.filter((transaction) => {
            if (filterOption === config_1.TRANSACTION_FILTERS.CUSTOMER_NAME) {
                const fullName = (0, utils_1.getFullName)(transaction?.name).toLowerCase();
                return fullName.includes(searchField.toLowerCase());
            }
            else if (filterOption === config_1.TRANSACTION_FILTERS.ACCOUNT_NUMBER) {
                return transaction.alphaeonAccountNumber.includes(searchField);
            }
        });
        return filteredData;
    };
    const handlePendingTransactionFilter = (values) => {
        const data = handleFilters(values, scheduledPendingTransaction);
        setDisplayedPendingTransactions(data);
    };
    const handleNextDayFilter = ({ searchField, filterOption, }) => {
        const data = handleFilters({ searchField, filterOption }, nextDayTransactions);
        setDisplayedNextDayTransactions(data);
    };
    const fetchSalesReport = (values = {
        startDate: undefined,
        endDate: undefined,
    }) => {
        const reportCriteria = {
            startDate: (0, date_fns_tz_1.formatInTimeZone)(values.startDate || startDate, config_1.PST_TIMEZONE, 'yyyy-MM-dd'),
            endDate: (0, date_fns_tz_1.formatInTimeZone)(values.endDate || endDate, config_1.PST_TIMEZONE, 'yyyy-MM-dd'),
        };
        const data = {
            postMerchantManagementV1ReportsSales: {
                ...reportCriteria,
                locationIds: [locationId],
            },
        };
        postSalesReports(data);
    };
    // Memos
    const accessLoanTransactionsForNextDay = (0, react_1.useMemo)(() => {
        return salesReports?.transactions
            .filter((transaction) => {
            const productType = (0, utils_1.mapTransactionTypeandInstrumentTypeToProductType)(transaction.transactionType, transaction.instrumentType);
            if (productType === config_1.PRODUCT_TYPE_MAP.installment) {
                return transaction;
            }
        })
            .sort((a, b) => new Date(b.installmentServiceDate).getTime() -
            new Date(a.installmentServiceDate).getTime());
    }, [salesReports]);
    const lineOfCreditTransactionsForNextDay = (0, react_1.useMemo)(() => {
        return salesReports?.transactions
            .filter((transaction) => {
            const productType = (0, utils_1.mapTransactionTypeandInstrumentTypeToProductType)(transaction.transactionType, transaction.instrumentType);
            if (productType === config_1.PRODUCT_TYPE_MAP.line_of_credit) {
                return transaction;
            }
        })
            .sort((a, b) => new Date(b.effectiveAt ?? 0).getTime() -
            new Date(a.effectiveAt ?? 0).getTime());
    }, [salesReports]);
    const scheduledPendingTransaction = (0, react_1.useMemo)(() => {
        const lineOfCreditTransactionsForScheduledPending = salesReports?.transactions.filter((transaction) => {
            return (config_1.PRODUCT_TYPE_MAP.line_of_credit ===
                (0, utils_1.mapTransactionTypeandInstrumentTypeToProductType)(transaction.transactionType, transaction.instrumentType) && config_1.TRANSACTION_STATUS.PENDING_SIGNATURE === transaction?.status);
        });
        const accessLoanTransactionsForScheduledPending = salesReports?.transactions.filter((transaction) => {
            (0, utils_1.mapTransactionTypeandInstrumentTypeToProductType)(transaction.transactionType, transaction.instrumentType);
            return (config_1.PRODUCT_TYPE_MAP.installment ===
                (0, utils_1.mapTransactionTypeandInstrumentTypeToProductType)(transaction.transactionType, transaction.instrumentType) &&
                (config_1.TRANSACTION_STATUS.PENDING_SIGNATURE === transaction?.status ||
                    config_1.TRANSACTION_STATUS.SIGNED === transaction?.status) &&
                !shouldShowNextDayPendingAccessLoanTransaction(transaction));
        });
        const mergedTransaction = new Array()
            .concat(lineOfCreditTransactionsForScheduledPending, accessLoanTransactionsForScheduledPending)
            .sort((a, b) => new Date(b.effectiveAt).getTime() - new Date(a.effectiveAt).getTime());
        return mergedTransaction.filter(Boolean) || [];
    }, [salesReports]);
    const nextDayTransactions = (0, react_1.useMemo)(() => {
        const mergedTransaction = new Array().concat(lineOfCreditTransactionsForNextDay?.filter((transaction) => {
            return (transaction?.effectiveAt &&
                transaction?.status &&
                (0, utils_1.checkIfESTTimeisPast10Pm)(transaction?.effectiveAt) &&
                config_1.LOC_NEXT_DAY_PENDING_STATES.includes(transaction?.status));
        }), accessLoanTransactionsForNextDay?.filter((transaction) => {
            return (transaction != undefined &&
                shouldShowNextDayPendingAccessLoanTransaction(transaction));
        }));
        return mergedTransaction.filter(Boolean) || [];
    }, [accessLoanTransactionsForNextDay, lineOfCreditTransactionsForNextDay]);
    const footerTotalNextDayPend = (0, react_1.useMemo)(() => {
        return (nextDayTransactions &&
            (0, utils_1.formatCurrency)((0, utils_1.calcTransaction)(nextDayTransactions), 2));
    }, [nextDayTransactions]);
    const footerTotalSchedPend = (0, react_1.useMemo)(() => {
        return (scheduledPendingTransaction &&
            (0, utils_1.formatCurrency)((0, utils_1.calcTransaction)(scheduledPendingTransaction), 2));
    }, [scheduledPendingTransaction]);
    (0, react_1.useEffect)(() => {
        fetchSalesReport();
    }, [locationId]);
    // Effects
    (0, react_1.useEffect)(() => {
        setDisplayedNextDayTransactions(nextDayTransactions);
    }, [nextDayTransactions]);
    (0, react_1.useEffect)(() => {
        setDisplayedPendingTransactions(scheduledPendingTransaction);
    }, [scheduledPendingTransaction]);
    return {
        displayedNextDayTransactions,
        displayedPendingTransactions,
        handleNextDayFilter,
        handlePendingTransactionFilter,
        footerTotalNextDayPend,
        footerTotalSchedPend,
        salesReportError,
        salesReportLoading,
    };
};
exports.default = useSalesTable;
